import { EditOutlined } from '@ant-design/icons';
import { useOne } from '@refinedev/core';
import { Button, Flex, Form, Modal, Skeleton } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import LinkTextField from '@/components/general/LinkTextField';
import { COMPANY_URLS } from '@/constants/urls';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useSelectScrollSearch } from '@/hooks/useSelectScrollSearch';
import { useSelectPropsWithInitial } from '@/hooks/useSelectWithInitialValue';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Reception } from '@/model/Logistic/Reception/Reception';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

interface ReceptionShowTechnicianProps {
  record?: Reception;
  refetch?: () => void;
}

const ReceptionShowTechnician = (props: ReceptionShowTechnicianProps) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissionContext();
  const [openModal, setOpenModal] = useState(false);
  const { record, refetch } = props;
  const {
    result: technicianResult,
    query: { isLoading: technicianIsLoading },
  } = useOne({
    resource: 'empresas',
    id: record?.technician || '',
    queryOptions: {
      enabled: !!record && !!record.technician,
      retry: false,
    },
  });

  return (
    <div className="ReceptionShowData">
      <Flex gap={4} align="center">
        <b>{t('Técnico involucrado')}</b>
        {hasPermission('edit') && record?.status == PACKAGE_STATES.PENDIENTE && (
          <>
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
                setOpenModal(true);
              }}
            />
            <Modal
              open={openModal}
              onCancel={() => setOpenModal(false)}
              footer={null}
              title={t('Cambiar técnico involucrado')}>
              <UpdateTechnicianModal
                record={record}
                closeModal={() => setOpenModal(false)}
                refetch={refetch}
              />
            </Modal>
          </>
        )}
      </Flex>
      {technicianIsLoading ? (
        <Skeleton />
      ) : (
        <>
          {record?.technician ? (
            <LinkTextField
              label={`${technicianResult?.full_name} (${technicianResult?.id})`}
              to={`${COMPANY_URLS.ADMIN_EMPRESA}/${technicianResult?.id}`}
              admin
            />
          ) : (
            '-'
          )}
        </>
      )}
    </div>
  );
};

interface UpdateModalProps {
  record?: Reception;
  closeModal: () => void;
  refetch?: () => void;
}
const UpdateTechnicianModal = (props: UpdateModalProps) => {
  const { t } = useTranslation();
  const { receptionRepository } = useRepositoriesContext();
  const { record, closeModal, refetch } = props;
  const { selectProps: technicianSelectProps } = useSelectScrollSearch({
    resource: 'empresas',
    optionLabel: (item: any) => item.full_name,
    meta: {
      type: 5,
      id: 'technician',
    },
    queryOptions: {
      queryKey: ['empresas', 'technicians'],
    },
  });
  const technicianSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: technicianSelectProps,
    resource: 'empresas',
    initialValue: record?.technician,
    labelKey: 'full_name',
    valueKey: 'id',
  });
  const [newTechnicianId, setNewTechnicianId] = useState<number | undefined>(record?.technician);
  const onConfirmTechnicianChange = useCallback(() => {
    if (!record?.id) return;

    if (newTechnicianId === record?.technician) {
      closeModal();

      return;
    }

    void receptionRepository.updateReceptionTechnician(record.id, newTechnicianId).then(() => {
      closeModal();
      refetch?.();
    });
  }, [closeModal, newTechnicianId, receptionRepository, record, refetch]);

  return (
    <div className="UpdateTechnicianModal">
      <Form layout="vertical" onFinish={onConfirmTechnicianChange}>
        <Form.Item label={t('Técnico involucrado')} required={true}>
          <AntdSelect
            {...technicianSelectPropsWithInitials}
            allowClear
            showSearch
            value={newTechnicianId}
            onChange={value => setNewTechnicianId(value as number | undefined)}
          />
        </Form.Item>
        <Flex justify="end" gap={8}>
          <Button onClick={closeModal}>{t('Cancelar')}</Button>
          <Button type="primary" htmlType="submit">
            {t('Guardar')}
          </Button>
        </Flex>
      </Form>
    </div>
  );
};

export default ReceptionShowTechnician;
